import { useEffect, type ReactNode } from "react";

import clsx from "clsx";
import { useKBar } from "kbar";
import { twMerge } from "tailwind-merge";

import {
  Modal as MModal,
  type ModalHeaderProps,
  type ModalRootProps,
  type ModalTitleProps,
} from "@mantine/core";

interface Props {
  onClose: () => void;
  isOpen: boolean;
  children: ReactNode;
  size?: ModalRootProps["size"] | "2xl" | "3xl";
  variant?: "v1" | "v2";
  titleContent?: ReactNode;
  headerProps?: ModalHeaderProps;
  headerTitleProps?: ModalTitleProps;
  hideCloseButton?: boolean;
  rootClassName?: string;
  overlayClassName?: string;
  innerClassName?: string;
  bodyClassName?: string;
  contentClassName?: string;
  closeOnClickOutside?: boolean;
  closeOnEscape?: boolean;
}

export function Modal({
  onClose,
  isOpen,
  children,
  titleContent,
  size = "lg",
  variant = "v1",
  headerProps,
  headerTitleProps,
  hideCloseButton,
  rootClassName,
  overlayClassName,
  innerClassName,
  bodyClassName,
  contentClassName,
  closeOnClickOutside = true,
  closeOnEscape = true,
}: Props) {
  const { query } = useKBar();

  useEffect(() => {
    if (isOpen) {
      query && query.disable(true);
    } else {
      query && query.disable(false);
    }

    return () => query && query.disable(false);
  }, [query, isOpen]);

  let reconciledSize = size;

  if (size === "2xl") {
    reconciledSize = "min(90%, 1200px)";
  }

  if (size === "3xl") {
    reconciledSize = "min(100%, 1600px)";
  }

  if (variant === "v2") {
    reconciledSize = "100%";
  }

  return (
    <MModal.Root
      opened={isOpen}
      onClose={onClose}
      centered
      size={reconciledSize}
      zIndex={2001}
      closeOnClickOutside={closeOnClickOutside}
      closeOnEscape={closeOnEscape}
      classNames={{
        root: clsx(rootClassName),
        overlay: twMerge(
          clsx({
            "bg-[rgba(30,30,30,0.47)]": variant === "v1",
            "bg-v2-interstellar/600/25%": variant === "v2",
          }),
          overlayClassName,
        ),
        inner: twMerge(clsx({ "p-5": variant === "v2" }), innerClassName),
        content: twMerge(
          "rounded-lg bg-v2-primary",
          clsx({
            "bg-v2-glass-inverse elevation-1-v2 overflow-hidden h-full backdrop-blur-[1px]":
              variant === "v2",
          }),
          contentClassName,
        ),
        body: twMerge(
          clsx({
            "px-6 pb-5": variant === "v1",
            "p-1.5 overflow-hidden h-full": variant === "v2",
          }),
          bodyClassName,
        ),
        close: clsx({
          "absolute right-0 top-0 w-8 h-8 mr-4 mt-4 z-[1]": variant === "v2",
          "text-v2-cosmic/400 bg-transparent hover:text-v2-cosmic/600":
            variant === "v2",
        }),
      }}
    >
      <MModal.Overlay />
      <MModal.Content>
        {variant === "v1" && (titleContent || !hideCloseButton) && (
          <MModal.Header
            className="px-6 bg-v2-primary text-v2-primary"
            {...headerProps}
          >
            {titleContent && (
              <MModal.Title {...headerTitleProps}>{titleContent}</MModal.Title>
            )}
            {!hideCloseButton && <MModal.CloseButton />}
          </MModal.Header>
        )}
        <MModal.Body className="text-v2-primary">
          {variant === "v2" && !hideCloseButton && (
            <MModal.CloseButton iconSize="15px" className="z-[9]" />
          )}
          {children}
        </MModal.Body>
      </MModal.Content>
    </MModal.Root>
  );
}
